import { AppThunkAction } from '../reducers';

import * as Services from '../../services/Specialties.Services';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/Specialties.Actions';
import { ErrorType } from '../../models/HttpError';
import { SpecialtyCreateCommand, SpecialtyUpdateCommand, SpecialtyViewModel } from '../../models/Entities/Specialties/SpecialtyModels';

// Union of known actions for specialties
export type KnownAction =
    | Actions.Specialties.SpecialtiesGetAllRequestAction
    | Actions.Specialties.SpecialtiesGetAllSuccessAction
    | Actions.Specialties.SpecialtiesGetAllFailureAction
    | Actions.Specialties.SpecialtiesGetByIdRequestAction
    | Actions.Specialties.SpecialtiesGetByIdSuccessAction
    | Actions.Specialties.SpecialtiesGetByIdFailureAction
    | Actions.Specialties.SpecialtiesCreateRequestAction
    | Actions.Specialties.SpecialtiesCreateSuccessAction
    | Actions.Specialties.SpecialtiesCreateFailureAction
    | Actions.Specialties.SpecialtiesUpdateRequestAction
    | Actions.Specialties.SpecialtiesUpdateSuccessAction
    | Actions.Specialties.SpecialtiesUpdateFailureAction
    | Actions.Specialties.SpecialtiesDeleteRequestAction
    | Actions.Specialties.SpecialtiesDeleteSuccessAction
    | Actions.Specialties.SpecialtiesDeleteFailureAction;

export const actionCreators = {
    GetAllSpecialties: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({
                type: ActionEvents.SPECIALTIES_GETALL_FAILURE,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({ type: ActionEvents.SPECIALTIES_GETALL_REQUEST });

        Services.GetAllSpecialties(token)
            .then((response) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_GETALL_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_GETALL_FAILURE,
                    error
                });
            });
    },

    GetSpecialtyById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({
                type: ActionEvents.SPECIALTIES_GETBYID_FAILURE,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.SPECIALTIES_GETBYID_REQUEST,
            id
        });

        Services.GetSpecialtyById(token, id)
            .then((response) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_GETBYID_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_GETBYID_FAILURE,
                    error
                });
            });
    },

    CreateSpecialty: (payload: SpecialtyCreateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({
                type: ActionEvents.SPECIALTIES_CREATE_FAILURE,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.SPECIALTIES_CREATE_REQUEST,
            payload
        });

        Services.CreateSpecialty(token, payload)
            .then((created: SpecialtyViewModel) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_CREATE_SUCCESS,
                    payload: created
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_CREATE_FAILURE,
                    error
                });
            });
    },

    UpdateSpecialty: (payload: SpecialtyUpdateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({
                type: ActionEvents.SPECIALTIES_UPDATE_FAILURE,
                id: payload.id,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.SPECIALTIES_UPDATE_REQUEST,
            payload
        });

        Services.UpdateSpecialty(token, payload)
            .then((updated: SpecialtyViewModel) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_UPDATE_SUCCESS,
                    payload: updated
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_UPDATE_FAILURE,
                    id: payload.id,
                    error
                });
            });
    },

    DeleteSpecialty: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({
                type: ActionEvents.SPECIALTIES_DELETE_FAILURE,
                id,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.SPECIALTIES_DELETE_REQUEST,
            id
        });

        Services.DeleteSpecialty(token, id)
            .then(() => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_DELETE_SUCCESS,
                    id
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.SPECIALTIES_DELETE_FAILURE,
                    id,
                    error
                });
            });
    }
};
